<script setup lang="tsx">
import { ref, onMounted, watch } from 'vue'
import { useI18n } from 'vue-i18n'
import http from '@/services/http'
import type { Response, AssetRisk } from '@/models/api-generated'

const props = defineProps<{ assetId: number }>()
const { t } = useI18n()
const risks = ref<AssetRisk[]>()

async function getRisks() {
	const { data } = await http.get<Response<AssetRisk[]>>(`api/risk/${props.assetId}`)
	if (data.data?.length) risks.value = data.data
}

function parseDescriptionParts(text: string): (string | { italic: string })[] {
	const parts: (string | { italic: string })[] = []
	const regex = /<em>(.*?)<\/em>/g
	let lastIndex = 0
	let match: RegExpExecArray | null

	while ((match = regex.exec(text)) !== null) {
		if (match.index > lastIndex) {
			parts.push(text.slice(lastIndex, match.index))
		}
		parts.push({ italic: match[1] })
		lastIndex = regex.lastIndex
	}
	if (lastIndex < text.length) {
		parts.push(text.slice(lastIndex))
	}
	return parts
}

watch(() => props.assetId, getRisks)

onMounted(async () => {
	getRisks()
})
</script>

<template>
	<template v-if="risks?.length">
		<div v-for="(risk, index) in risks" :key="index" class="row mt-3 risk-container">
			<div class="col-md-12">
				<h6>{{ risk.title }}</h6>
			</div>
			<div class="col-md-12" style="white-space: normal">
				<span v-for="(part, i) in parseDescriptionParts(risk.description)" :key="i">
					<template v-if="typeof part === 'string'">{{ part }}</template>
					<template v-else
						><i>{{ part.italic }}</i></template
					>
				</span>
			</div>
		</div>
	</template>
	<div v-else class="d-flex flex-sm-column flex-grow-1 text-center justify-content-center">
		<i class="bi bi-unlock-fill fs-5"></i>
		<p class="mb-0 msg-title">{{ t('asset.risks.none') }}</p>
		<p class="msg-desc">{{ t('asset.risks.connect') }}</p>
	</div>
</template>

<style lang="sass" scoped>
.fixed-badge
  max-height: 1.5rem
</style>
